import { decode as decodeBolt11 } from "@gandlaf21/bolt11-decode";

/**
 * The Lightning Address tester's checks (LUD-16 on top of LNURL-pay, LUD-06).
 * Everything here runs in the visitor's browser and talks to the address's own
 * server; callers must get a quota (toolQuota.ts) before each step.
 */

export type CheckStatus = "pass" | "fail" | "warn" | "info";

export interface Check {
  status: CheckStatus;
  label: string;
  detail?: string;
  /** what the address's provider needs to change (failures and warnings) */
  fix?: string;
}

export interface ParsedAddress {
  user: string;
  domain: string;
  /** user@domain as it will be tested */
  address: string;
}

export interface PayParams {
  callback: string;
  minSendable: number;
  maxSendable: number;
  metadata: string;
  description: string | null;
  thumbnail: string | null;
  commentAllowed: number;
}

export interface ResolveResult {
  url: string;
  checks: Check[];
  /** null when the address can't be paid, so no invoice test is offered */
  params: PayParams | null;
}

export interface TestInvoice {
  pr: string;
  amountSats: number | null;
  /** unix seconds */
  expiresAt: number | null;
}

export interface InvoiceResult {
  checks: Check[];
  invoice: TestInvoice | null;
}

const TIMEOUT_MS = 15000;
const USER_REGEX = /^[a-z0-9\-_.+]+$/;
const DOMAIN_REGEX = /^[a-z0-9-]+(\.[a-z0-9-]+)+(:\d{1,5})?$/i;

const CORS_FIX =
  "Send an Access-Control-Allow-Origin: * header on /.well-known/lnurlp/ and on the callback URL.";

/** Trim, drop a "lightning:" prefix and split into user / domain. Returns an error string when it isn't usable. */
export function parseLightningAddress(input: string): ParsedAddress | string {
  const cleaned = input.trim().replace(/^lightning:/i, "");
  const parts = cleaned.split("@");
  if (parts.length !== 2 || !parts[0] || !parts[1]) {
    return "A Lightning Address looks like name@domain.com.";
  }
  const user = parts[0];
  const domain = parts[1].toLowerCase();
  if (domain.endsWith(".onion")) {
    return "Onion (.onion) addresses can't be tested from a normal browser.";
  }
  if (!DOMAIN_REGEX.test(domain)) {
    return `"${domain}" doesn't look like a domain name.`;
  }
  if (/[\s/?#]/.test(user) || user.length > 64) {
    return "The part before the @ can't contain spaces, slashes, ? or #.";
  }
  return { user, domain, address: `${user}@${domain}` };
}

/** Format millisatoshis as sats, keeping a fraction only when there is one. */
export function msatToSatsText(msat: number): string {
  const sats = msat / 1000;
  return (
    sats.toLocaleString(undefined, { maximumFractionDigits: 3 }) +
    (sats === 1 ? " sat" : " sats")
  );
}

async function fetchWithTimeout(
  url: string,
  init: RequestInit = {},
): Promise<Response> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
  try {
    return await fetch(url, {
      ...init,
      signal: controller.signal,
      cache: "no-store",
    });
  } finally {
    clearTimeout(timer);
  }
}

const isAbort = (err: any) => err?.name === "AbortError";

/** Parse a JSON body, or null when it isn't JSON. */
function parseJson(text: string): any {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const snippet = (text: string) =>
  text.length > 120 ? text.slice(0, 120) + "…" : text;

/** Step 1 (LUD-16): GET https://domain/.well-known/lnurlp/user and check the answer. */
export async function resolveLightningAddress(
  parsed: ParsedAddress,
): Promise<ResolveResult> {
  const { user, domain, address } = parsed;
  const url = `https://${domain}/.well-known/lnurlp/${encodeURIComponent(user)}`;
  const checks: Check[] = [];
  const done = (): ResolveResult => ({ url, checks, params: null });

  if (USER_REGEX.test(user)) {
    checks.push({ status: "pass", label: "Address format is valid" });
  } else if (USER_REGEX.test(user.toLowerCase())) {
    checks.push({
      status: "warn",
      label: "The name has capital letters",
      detail:
        "LUD-16 names are lowercase. Many wallets lowercase what's typed, so this only works if the server also accepts the lowercase name.",
      fix: "Accept the lowercase form of every name.",
    });
  } else {
    checks.push({
      status: "warn",
      label: "The name has characters LUD-16 doesn't allow",
      detail:
        "LUD-16 names may only use a-z, 0-9, - _ . and +. Some wallets will refuse this address.",
      fix: "Offer a name made only of a-z 0-9 - _ . +",
    });
  }

  // 1. fetch it the way a web wallet would
  let response: Response;
  try {
    response = await fetchWithTimeout(url, {
      headers: { Accept: "application/json" },
    });
  } catch (err) {
    if (isAbort(err)) {
      checks.push({
        status: "fail",
        label: "The server didn't answer within 15 seconds",
        detail: url,
        fix: "Check that the server is up and answering on /.well-known/lnurlp/.",
      });
      return done();
    }
    // A fetch that fails is either "can't connect" or "connected, but CORS hides the
    // answer". The browser won't say which, but an opaque no-cors request only
    // succeeds when the server is reachable.
    let reachable = false;
    try {
      await fetchWithTimeout(url, { mode: "no-cors" });
      reachable = true;
    } catch {
      reachable = false;
    }
    if (reachable) {
      checks.push({ status: "pass", label: "The server is reachable over HTTPS" });
      checks.push({
        status: "fail",
        label: "Your browser couldn't read the response (CORS)",
        detail:
          "The server answered, but it doesn't send an Access-Control-Allow-Origin header, so browsers aren't allowed to read it. Web wallets and browser extensions will fail to pay this address; phone wallets may still work.",
        fix: CORS_FIX,
      });
    } else {
      checks.push({
        status: "fail",
        label: `Couldn't reach ${domain}`,
        detail:
          "The domain may be misspelled, its DNS or TLS certificate may be broken, or the server is down.",
        fix: `Check that https://${domain}/.well-known/lnurlp/ is served over valid HTTPS.`,
      });
    }
    return done();
  }

  checks.push({ status: "pass", label: "The server is reachable over HTTPS" });
  checks.push({
    status: "pass",
    label: "Browsers can read the response (CORS header present)",
  });
  if (response.redirected) {
    checks.push({
      status: "info",
      label: "The request was redirected",
      detail: response.url,
    });
  }

  const text = await response.text();
  const data = parseJson(text);

  if (!response.ok) {
    const reason =
      data && typeof data.reason === "string" ? ` It says: "${data.reason}"` : "";
    checks.push({
      status: "fail",
      label: `The server answered HTTP ${response.status}`,
      detail:
        (response.status === 404
          ? "It doesn't know this name: it may be misspelled, or the account no longer exists."
          : "The address can't be paid while the server returns an error.") +
        reason,
      fix: "Return HTTP 200 with the LNURL-pay JSON for this name.",
    });
    return done();
  }

  if (data === null || typeof data !== "object") {
    checks.push({
      status: "fail",
      label: "The response isn't valid JSON",
      detail: snippet(text) || "(empty response)",
      fix: "Return the LNURL-pay JSON described in LUD-06.",
    });
    return done();
  }
  checks.push({ status: "pass", label: "The response is valid JSON" });

  if (data.status === "ERROR") {
    checks.push({
      status: "fail",
      label: "The server returned an error",
      detail: typeof data.reason === "string" ? data.reason : undefined,
    });
    return done();
  }

  if (data.tag === "payRequest") {
    checks.push({ status: "pass", label: 'It\'s an LNURL-pay response (tag "payRequest")' });
  } else {
    checks.push({
      status: "fail",
      label: 'The tag isn\'t "payRequest"',
      detail: `tag: ${JSON.stringify(data.tag ?? null)}`,
      fix: 'Set "tag": "payRequest".',
    });
    return done();
  }

  // 2. callback
  let callbackOk = false;
  let callbackHost = "";
  try {
    const callbackUrl = new URL(String(data.callback));
    callbackHost = callbackUrl.host;
    if (callbackUrl.protocol === "https:") {
      callbackOk = true;
      checks.push({ status: "pass", label: "The callback URL uses HTTPS" });
    } else {
      checks.push({
        status: "fail",
        label: "The callback URL isn't HTTPS",
        detail: String(data.callback),
        fix: "Serve the callback over HTTPS.",
      });
    }
  } catch {
    checks.push({
      status: "fail",
      label: "The callback is missing or isn't a URL",
      detail: `callback: ${JSON.stringify(data.callback ?? null)}`,
      fix: "Return the URL wallets should call for an invoice in \"callback\".",
    });
  }
  if (callbackOk && callbackHost !== domain) {
    checks.push({
      status: "info",
      label: `Invoices come from ${callbackHost}`,
      detail: "The callback is on a different host from the address. That's allowed.",
    });
  }

  // 3. amounts
  const min = Number(data.minSendable);
  const max = Number(data.maxSendable);
  let amountsOk = false;
  if (!Number.isInteger(min) || !Number.isInteger(max) || min < 1) {
    checks.push({
      status: "fail",
      label: "minSendable / maxSendable are missing or invalid",
      detail: `minSendable: ${JSON.stringify(data.minSendable ?? null)}, maxSendable: ${JSON.stringify(data.maxSendable ?? null)}`,
      fix: "Return whole numbers of millisatoshis in minSendable and maxSendable.",
    });
  } else if (min > max) {
    checks.push({
      status: "fail",
      label: "minSendable is larger than maxSendable",
      detail: `${msatToSatsText(min)} > ${msatToSatsText(max)}`,
      fix: "minSendable must be ≤ maxSendable.",
    });
  } else if (max < 1000) {
    checks.push({
      status: "fail",
      label: "It can't receive even 1 sat",
      detail: `maxSendable is ${max} msat`,
    });
  } else {
    amountsOk = true;
    checks.push({
      status: "pass",
      label: `Accepts ${msatToSatsText(min)} to ${msatToSatsText(max)}`,
    });
  }

  // 4. metadata
  let metadataOk = false;
  let description: string | null = null;
  let thumbnail: string | null = null;
  const metadata = data.metadata;
  const entries = typeof metadata === "string" ? parseJson(metadata) : null;
  if (typeof metadata !== "string") {
    checks.push({
      status: "fail",
      label: "The metadata isn't a JSON string",
      detail:
        metadata === undefined
          ? "metadata is missing"
          : "metadata must be a string containing JSON, not a JSON object.",
      fix: "Return metadata as a string, e.g. \"[[\\\"text/plain\\\",\\\"…\\\"]]\".",
    });
  } else if (!Array.isArray(entries)) {
    checks.push({
      status: "fail",
      label: "The metadata string isn't a JSON array",
      detail: snippet(metadata),
      fix: "metadata must be a JSON array of [type, content] pairs.",
    });
  } else {
    let identifier: string | null = null;
    for (const entry of entries) {
      if (!Array.isArray(entry) || entry.length < 2) continue;
      const [kind, value] = entry;
      if (kind === "text/plain") description = String(value);
      if (kind === "text/identifier" || kind === "text/email") identifier = String(value);
      if (kind === "image/png;base64" || kind === "image/jpeg;base64") {
        thumbnail = thumbnail ?? `data:${kind.split(";")[0]};base64,${value}`;
      }
    }
    if (description !== null) {
      metadataOk = true;
      checks.push({
        status: "pass",
        label: "The metadata has a description",
        detail: description ? `"${snippet(description)}"` : "(empty)",
      });
    } else {
      checks.push({
        status: "fail",
        label: "The metadata has no text/plain description",
        detail: "LUD-06 requires one.",
        fix: 'Add ["text/plain", "…"] to metadata.',
      });
    }
    if (identifier === null) {
      checks.push({
        status: "warn",
        label: "The metadata doesn't name the address",
        detail:
          "LUD-16 asks for a text/identifier (or text/email) entry with the address. Most wallets pay without it.",
        fix: `Add ["text/identifier", "${address}"] to metadata.`,
      });
    } else if (identifier.toLowerCase() !== address.toLowerCase()) {
      checks.push({
        status: "warn",
        label: "The metadata names a different address",
        detail: `text/identifier is "${snippet(identifier)}"`,
      });
    } else {
      checks.push({ status: "pass", label: "The metadata names this address" });
    }
    if (thumbnail) {
      checks.push({ status: "info", label: "Has a picture in its metadata" });
    }
  }

  // 5. extras
  const commentAllowed = Number.isInteger(data.commentAllowed)
    ? Math.max(0, data.commentAllowed)
    : 0;
  if (commentAllowed > 0) {
    checks.push({
      status: "info",
      label: `Accepts comments up to ${commentAllowed} characters (LUD-12)`,
    });
  }
  if (data.allowsNostr === true) {
    if (typeof data.nostrPubkey === "string" && /^[0-9a-f]{64}$/i.test(data.nostrPubkey)) {
      checks.push({ status: "info", label: "Supports Nostr zaps (NIP-57)" });
    } else {
      checks.push({
        status: "warn",
        label: "Says it supports Nostr zaps, but nostrPubkey isn't a valid key",
        detail: `nostrPubkey: ${JSON.stringify(data.nostrPubkey ?? null)}`,
        fix: "Set nostrPubkey to the 64-character hex key that signs zap receipts.",
      });
    }
  }
  if (data.payerData && typeof data.payerData === "object") {
    const fields = Object.keys(data.payerData);
    const mandatory = fields.filter((key) => data.payerData[key]?.mandatory);
    checks.push({
      status: mandatory.length ? "warn" : "info",
      label: `Asks for payer data (LUD-18): ${fields.join(", ") || "none"}`,
      detail: mandatory.length
        ? `Required: ${mandatory.join(", ")}. Wallets that don't support LUD-18 can't pay it.`
        : undefined,
    });
  }

  const params =
    callbackOk && amountsOk && metadataOk
      ? {
          callback: String(data.callback),
          minSendable: min,
          maxSendable: max,
          metadata: metadata as string,
          description,
          thumbnail,
          commentAllowed,
        }
      : null;
  return { url, checks, params };
}

const toHex = (value: any): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value.toLowerCase();
  const bytes: Uint8Array =
    value instanceof Uint8Array ? value : new Uint8Array(value.data ?? value);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
};

async function sha256Hex(text: string): Promise<string | null> {
  if (!window.crypto?.subtle) return null;
  const digest = await window.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(text),
  );
  return toHex(new Uint8Array(digest));
}

/** Step 2 (LUD-06): GET callback?amount=<msat> and check the invoice that comes back. */
export async function requestTestInvoice(
  params: PayParams,
  amountMsat: number,
): Promise<InvoiceResult> {
  const checks: Check[] = [];
  const done = (): InvoiceResult => ({ checks, invoice: null });

  const url = new URL(params.callback);
  url.searchParams.set("amount", String(amountMsat));

  let response: Response;
  try {
    // no retry and no no-cors probe here: every request to the callback can make an invoice
    response = await fetchWithTimeout(url.toString(), {
      headers: { Accept: "application/json" },
    });
  } catch (err) {
    checks.push({
      status: "fail",
      label: isAbort(err)
        ? "The callback didn't answer within 15 seconds"
        : "Your browser couldn't read the callback's response",
      detail: isAbort(err)
        ? undefined
        : "Most often the callback doesn't send an Access-Control-Allow-Origin header (web wallets will fail too), or the server is down.",
      fix: isAbort(err) ? "Check that the invoice service is up." : CORS_FIX,
    });
    return done();
  }

  const text = await response.text();
  const data = parseJson(text);
  if (!response.ok || data === null || typeof data !== "object") {
    checks.push({
      status: "fail",
      label: response.ok
        ? "The callback's response isn't valid JSON"
        : `The callback answered HTTP ${response.status}`,
      detail:
        data && typeof data.reason === "string" ? data.reason : snippet(text) || undefined,
      fix: 'Return {"pr": "<invoice>", "routes": []}.',
    });
    return done();
  }
  if (data.status === "ERROR") {
    checks.push({
      status: "fail",
      label: "The callback returned an error instead of an invoice",
      detail: typeof data.reason === "string" ? data.reason : undefined,
    });
    return done();
  }
  if (typeof data.pr !== "string" || !data.pr) {
    checks.push({
      status: "fail",
      label: "The callback's response has no invoice (pr)",
      detail: snippet(text),
      fix: 'Return the BOLT11 invoice in "pr".',
    });
    return done();
  }
  checks.push({ status: "pass", label: "The callback returned an invoice" });

  const pr = data.pr.trim();
  let decoded: any;
  try {
    decoded = decodeBolt11(pr);
  } catch {
    checks.push({
      status: "fail",
      label: "The invoice isn't a valid BOLT11 invoice",
      detail: snippet(pr),
    });
    return done();
  }
  const section = (name: string) =>
    decoded.sections.find((s: any) => s.name === name)?.value;

  // network
  const network = section("coin_network")?.bech32;
  if (network === "bc") {
    checks.push({ status: "pass", label: "It's a mainnet invoice" });
  } else {
    checks.push({
      status: "fail",
      label: "It isn't a mainnet invoice",
      detail: `Network prefix "ln${network ?? "?"}". Real bitcoin can't pay it.`,
    });
  }

  // amount: LUD-06 says the wallet MUST check it equals the amount it asked for
  const invoiceMsat = section("amount");
  const amountSats =
    invoiceMsat != null ? Number(String(invoiceMsat)) / 1000 : null;
  if (invoiceMsat == null) {
    checks.push({
      status: "fail",
      label: "The invoice has no amount",
      detail: "LUD-06 wallets must refuse an invoice whose amount differs from the one requested.",
      fix: "Make the invoice for exactly the amount= the wallet asked for.",
    });
  } else if (String(invoiceMsat) === String(amountMsat)) {
    checks.push({
      status: "pass",
      label: `The amount is right: ${msatToSatsText(amountMsat)}`,
    });
  } else {
    checks.push({
      status: "fail",
      label: "The invoice amount is different from the amount asked for",
      detail: `Asked for ${msatToSatsText(amountMsat)}, got ${msatToSatsText(Number(String(invoiceMsat)))}. Wallets must refuse it.`,
      fix: "Make the invoice for exactly the amount= the wallet asked for, in millisatoshis.",
    });
  }

  // description hash = sha256(metadata)
  const invoiceHash = toHex(section("description_hash"));
  const expectedHash = await sha256Hex(params.metadata);
  if (!invoiceHash) {
    checks.push({
      status: "fail",
      label: "The invoice has no description hash",
      detail:
        section("description") != null
          ? "It carries a plain description instead. LUD-06 requires the SHA-256 of the metadata, and strict wallets refuse to pay without it."
          : "LUD-06 requires the SHA-256 of the metadata, and strict wallets refuse to pay without it.",
      fix: "Set the invoice's description_hash to sha256(metadata string, UTF-8).",
    });
  } else if (expectedHash === null) {
    checks.push({
      status: "info",
      label: "Couldn't check the description hash in this browser",
    });
  } else if (invoiceHash === expectedHash) {
    checks.push({
      status: "pass",
      label: "The description hash matches the metadata",
    });
  } else {
    checks.push({
      status: "fail",
      label: "The description hash doesn't match the metadata",
      detail: `Expected sha256(metadata) = ${expectedHash.slice(0, 16)}…, the invoice has ${invoiceHash.slice(0, 16)}…. Strict wallets refuse to pay it.`,
      fix: "Hash the exact metadata string you return in step 1 (UTF-8), byte for byte.",
    });
  }

  // expiry
  const timestamp = Number(section("timestamp"));
  const expirySeconds = Number(section("expiry") ?? 3600);
  const expiresAt = Number.isFinite(timestamp) ? timestamp + expirySeconds : null;
  const now = Math.floor(Date.now() / 1000);
  if (expiresAt === null) {
    checks.push({ status: "warn", label: "Couldn't read the invoice's expiry" });
  } else if (expiresAt <= now) {
    checks.push({
      status: "fail",
      label: "The invoice is already expired",
      detail: "Check the server's clock and the invoice expiry.",
    });
  } else if (expirySeconds < 60) {
    checks.push({
      status: "warn",
      label: `The invoice expires after only ${expirySeconds} seconds`,
      detail: "Slow wallets or route finding may not make it in time.",
    });
  } else {
    checks.push({
      status: "pass",
      label: `The invoice is valid for ${formatDuration(expirySeconds)}`,
    });
  }

  // extras in the callback's response
  if (data.successAction && typeof data.successAction === "object") {
    checks.push({
      status: "info",
      label: `Shows a message after payment (successAction "${data.successAction.tag}")`,
    });
  }
  if (typeof data.verify === "string") {
    checks.push({
      status: "info",
      label: "Supports payment verification (LUD-21)",
    });
  }

  return { checks, invoice: { pr, amountSats, expiresAt } };
}

export function formatDuration(seconds: number): string {
  if (seconds >= 86400 && seconds % 3600 === 0) {
    const days = seconds / 86400;
    return Number.isInteger(days) ? `${days} day${days === 1 ? "" : "s"}` : `${seconds / 3600} hours`;
  }
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes ? `${hours} h ${minutes} min` : `${hours} hour${hours === 1 ? "" : "s"}`;
  }
  if (seconds >= 60) return `${Math.floor(seconds / 60)} min`;
  return `${seconds} seconds`;
}
